import JsSIP from 'jssip';
import { makePreferredReturn } from './sipPreferred.mjs';

let ua = null;
let currentSession = null;
let preferredReturn = null;   // brings SIP back to /sip-ws after a detour to the direct port

export const sipService = {
  connect({ extension, password, wsUrl, wsUrls, domain }) {
    if (ua) {
      try { ua.stop(); } catch (e) {}
    }

    // More than one address, in order of preference: JsSIP tries the next
    // socket when one will not open, which is how SIP survives a server that
    // does not proxy /sip-ws yet (SEC-ARCH-007). Higher weight goes first.
    const urls = (wsUrls && wsUrls.length ? wsUrls : [wsUrl]).filter(Boolean);
    const sockets = urls.map((url, i) => ({
      socket: new JsSIP.WebSocketInterface(url),
      weight: urls.length - i,
    }));
    console.log('[SIP] sockets, in order:', urls.join(', '));
    if (preferredReturn) preferredReturn.stop();
    ua = new JsSIP.UA({
      sockets,
      uri: `sip:${extension}@${domain}`,
      password,
      register: true,
      register_expires: 300,
      contact_uri: `sip:${extension}@${domain};transport=ws`,
      user_agent: 'Voxiox/1.0',
      session_timers: false,
    });

    // JsSIP stays on a fallback socket until THAT one fails. A detour to the
    // direct port must not become the rest of the session (sipPreferred.mjs).
    if (urls.length > 1) {
      const theUa = ua;
      const preferred = urls[0];
      preferredReturn = makePreferredReturn({
        preferred,
        isIdle: () => !currentSession,
        currentUrl: () => theUa.transport && theUa.transport.url,
        goBack: () => {
          const t = theUa.transport;
          if (!t || ua !== theUa) return;
          console.log('[SIP] back to the preferred socket:', preferred);
          // JsSIP has no "prefer again": clear the failure marks and start
          // the preferred socket; the UA re-registers on connect.
          t.sockets.forEach((s) => { s.status = 0; });
          t.disconnect();
          const home = t.sockets.find((s) => s.socket.url === preferred);
          if (home) t.socket = home.socket;
          t.connect();
        },
      });
      ua.on('connected', () => {
        const url = ua && ua.transport ? ua.transport.url : null;
        if (url && url !== preferred) console.warn('[SIP] connected on the fallback', url, '— will return to', preferred, 'when idle');
        preferredReturn.connected(url);
      });
    }

    return ua;
  },

  call({ extension, domain, localStream, iceServers, iceTransportPolicy, onStream, onEnded, onFailed }) {
    if (!ua) throw new Error('SIP not connected');

    const defaultIceServers = [
      { urls: `stun:${domain}:3478` },
    ];

    let streamFired = false;
    const fireStream = (stream) => {
      if (streamFired) return;
      streamFired = true;
      console.log('[SIP] Remote stream received');
      onStream && onStream(stream);
    };

    // Attach peerconnection listener BEFORE ua.call() — JsSIP may fire
    // the event synchronously when localStream is provided
    const pcHandler = (data) => {
      const pc = data.peerconnection || data.connection;
      if (!pc) {
        console.error('[SIP] peerconnection event fired but no PC object');
        return;
      }
      console.log('[SIP] PeerConnection created, ICE state:', pc.iceConnectionState);

      pc.oniceconnectionstatechange = () => {
        console.log('[SIP] ICE state:', pc.iceConnectionState);
        if (pc.iceConnectionState === 'failed') {
          console.error('[SIP] ICE connection failed');
          onFailed && onFailed({ cause: 'ICE Failed' });
        }
      };

      pc.onicegatheringstatechange = () => {
        console.log('[SIP] ICE gathering:', pc.iceGatheringState);
      };

      pc.onicecandidate = (ev) => {
        if (ev.candidate) {
          console.log('[SIP] ICE candidate:', ev.candidate.type, ev.candidate.protocol, ev.candidate.address);
        } else {
          console.log('[SIP] ICE gathering complete (null candidate)');
        }
      };

      pc.ontrack = (ev) => {
        console.log('[SIP] ontrack fired, streams:', ev.streams?.length, 'track:', ev.track?.kind);
        if (ev.streams?.[0]) {
          fireStream(ev.streams[0]);
        } else if (ev.track) {
          const stream = new MediaStream([ev.track]);
          fireStream(stream);
        }
      };

      pc.addEventListener('addstream', (ev) => {
        console.log('[SIP] addstream fired');
        fireStream(ev.stream);
      });
    };

    // Listen on the UA for newRTCSession to catch peerconnection early
    const sessionHandler = (data) => {
      if (data.originator === 'local') {
        data.session.on('peerconnection', pcHandler);
        // JsSIP waits indefinitely for ICE gathering to complete.
        // With iceTransportPolicy:'relay', TURN allocation can be slow (especially
        // turns: TLS). Tell JsSIP to proceed as soon as the first candidate arrives
        // instead of waiting for ALL transports to respond.
        data.session.on('icecandidate', (evt) => {
          console.log('[SIP] icecandidate ready — proceeding with INVITE');
          evt.ready();
        });
      }
    };
    ua.on('newRTCSession', sessionHandler);

    const servers = iceServers || defaultIceServers;
    console.log('[SIP] ICE servers:', JSON.stringify(servers.map(s => s.urls || s)),
      'policy:', iceTransportPolicy || 'all');

    const session = ua.call(`sip:${extension}@${domain}`, {
      mediaConstraints: { audio: true, video: false },
      mediaStream: localStream,
      rtcOfferConstraints: { offerToReceiveAudio: true },
      pcConfig: {
        iceServers: servers,
        iceTransportPolicy: iceTransportPolicy || 'all',
        iceCandidatePoolSize: 0,
      },
      sessionTimersExpires: 120,
    });

    // Also attach directly in case newRTCSession already fired
    session.on('peerconnection', pcHandler);
    session.on('icecandidate', (evt) => {
      const ld = session.connection && session.connection.localDescription;
      console.log('[SIP] icecandidate:', evt.candidate && evt.candidate.candidate ? evt.candidate.candidate.split(' ').slice(4, 8).join(' ') : '(none)',
        '| candidates in localDescription:', ld ? (ld.sdp.match(/a=candidate/g) || []).length : 'no LD');
      evt.ready();
    });
    // What actually goes on the wire: Asterisk 22 believes the peer has ICE
    // only if the SDP carries at least one a=candidate (check_ice_support).
    session.on('sdp', (e) => {
      const n = (e.sdp.match(/a=candidate/g) || []).length;
      console.log('[SIP] sdp', e.originator, e.type, '— candidates:', n, n === 0 ? '(NONE — Asterisk will answer without ICE)' : '');
    });

    // If PeerConnection already exists (was created synchronously), attach listeners now
    if (session.connection) {
      console.log('[SIP] PeerConnection already exists, attaching listeners directly');
      pcHandler({ peerconnection: session.connection });
    }

    currentSession = session;

    session.on('confirmed', () => {
      console.log('[SIP] Call confirmed (answered)');
      // Double-check: if PC exists but listeners weren't attached yet
      if (session.connection && !streamFired) {
        const pc = session.connection;
        console.log('[SIP] Post-confirm ICE state:', pc.iceConnectionState);
        // If there are already remote tracks, fire stream
        const receivers = pc.getReceivers?.() || [];
        const tracks = receivers.filter(r => r.track).map(r => r.track);
        if (tracks.length > 0) {
          console.log('[SIP] Found', tracks.length, 'existing tracks');
          const stream = new MediaStream(tracks);
          fireStream(stream);
        }
      }
    });

    session.on('ended', () => {
      console.log('[SIP] Call ended');
      currentSession = null;
      onEnded && onEnded();
    });

    session.on('failed', (e) => {
      console.log('[SIP] Call failed:', e?.cause);
      currentSession = null;
      onFailed && onFailed(e);
    });

    // Cleanup UA listener
    ua.removeListener('newRTCSession', sessionHandler);

    return session;
  },

  answer({ iceServers, iceTransportPolicy, onStream, onEnded, onFailed }) {
    if (!currentSession) throw new Error('No incoming session');
    const session = currentSession;

    let streamFired = false;
    const fireStream = (stream) => {
      if (streamFired) return;
      streamFired = true;
      console.log('[SIP] Remote stream received (incoming)');
      onStream && onStream(stream);
    };

    const pcHandler = (data) => {
      const pc = data.peerconnection || data.connection;
      if (!pc) {
        console.error('[SIP] peerconnection event fired but no PC object (incoming)');
        return;
      }
      console.log('[SIP] PeerConnection created (incoming), ICE state:', pc.iceConnectionState);

      pc.oniceconnectionstatechange = () => {
        console.log('[SIP] ICE state (incoming):', pc.iceConnectionState);
        if (pc.iceConnectionState === 'failed') {
          console.error('[SIP] ICE connection failed (incoming)');
          onFailed && onFailed({ cause: 'ICE Failed' });
        }
      };

      pc.onicegatheringstatechange = () => {
        console.log('[SIP] ICE gathering (incoming):', pc.iceGatheringState);
      };

      pc.onicecandidate = (ev) => {
        if (ev.candidate) {
          console.log('[SIP] ICE candidate (incoming):', ev.candidate.type, ev.candidate.protocol, ev.candidate.address);
        } else {
          console.log('[SIP] ICE gathering complete (incoming, null candidate)');
        }
      };

      pc.ontrack = (ev) => {
        console.log('[SIP] ontrack fired (incoming), streams:', ev.streams?.length, 'track:', ev.track?.kind);
        if (ev.streams?.[0]) {
          fireStream(ev.streams[0]);
        } else if (ev.track) {
          fireStream(new MediaStream([ev.track]));
        }
      };

      pc.addEventListener('addstream', (ev) => {
        console.log('[SIP] addstream fired (incoming)');
        fireStream(ev.stream);
      });
    };

    // Listen for peerconnection BEFORE answer
    session.on('peerconnection', pcHandler);
    // Proceed with first ICE candidate (don't wait for all TURN transports)
    session.on('icecandidate', (evt) => {
      console.log('[SIP] icecandidate ready (incoming) — proceeding');
      evt.ready();
    });

    console.log('[SIP] Answering with ICE servers:', JSON.stringify((iceServers || []).map(s => s.urls || s)),
      'policy:', iceTransportPolicy || 'all');

    session.answer({
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers: iceServers || [{ urls: 'stun:stun.l.google.com:19302' }],
        iceTransportPolicy: iceTransportPolicy || 'all',
      }
    });

    // If PeerConnection already exists (synchronous creation)
    if (session.connection) {
      console.log('[SIP] PeerConnection already exists (incoming), attaching listeners');
      pcHandler({ peerconnection: session.connection });
    }

    session.on('confirmed', () => {
      console.log('[SIP] Incoming call confirmed');
      if (session.connection && !streamFired) {
        const pc = session.connection;
        console.log('[SIP] Post-confirm ICE state (incoming):', pc.iceConnectionState);
        const receivers = pc.getReceivers?.() || [];
        const tracks = receivers.filter(r => r.track).map(r => r.track);
        if (tracks.length > 0) {
          console.log('[SIP] Found', tracks.length, 'existing tracks (incoming)');
          fireStream(new MediaStream(tracks));
        }
      }
    });

    session.on('ended', () => {
      console.log('[SIP] Incoming call ended');
      currentSession = null;
      onEnded && onEnded();
    });

    session.on('failed', (e) => {
      console.log('[SIP] Incoming call failed:', e?.cause);
      currentSession = null;
      onFailed && onFailed(e);
    });
  },

  hangup() {
    if (currentSession) {
      try { currentSession.terminate(); } catch (e) {}
      currentSession = null;
    }
  },

  mute(muted) {
    if (!currentSession) return;
    if (muted) currentSession.mute();
    else currentSession.unmute();
  },

  getCurrentSession() { return currentSession; },

  getConnection() {
    if (!currentSession) return null;
    return currentSession.connection || null;
  },

  onIncoming(callback) {
    if (!ua) return;
    ua.on('newRTCSession', (data) => {
      if (data.originator === 'remote') {
        currentSession = data.session;
        callback(data.session);
      }
    });
  },

  disconnect() {
    sipService.hangup();
    if (ua) { try { ua.stop(); } catch (e) {} ua = null; }
  }
};

